import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { Box, useTheme, useMediaQuery, ThemeProvider } from '@mui/material';
import { appTheme } from 'common/theme';
import { PersonalInfoForm } from 'components/common/PersonalInfoForm';
import { TextHot } from 'components/common/TextHot';
import { generateTestId } from '../../../../app/functions';
import { addPopup } from '../../../../redux/actions';
import ReactHtmlParser from 'react-html-parser';
import { reactParserCommonOptions } from '../../../../app/functions';
import { useTranslations } from 'common/hooks/useTranslations';
import { OTP_PROCESS } from 'pages/personal-zone/utils/enums';
const RegistrationForm = ({
  text,
  onOtpStateChange,
  testID = 'registration-form',
  renderPhoneValidationExternally = false,
  onOtpRequest,
  onRegistrationResponse,
  onCloseAllModals,
}) => {
  const dispatch = useDispatch();
  const { translate } = useTranslations();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isOtpActive, setIsOtpActive] = useState(false);
  const citiesArr = useSelector((state) => state.config.citiesArr);
  const work_places = useSelector((state) => state.config.WorkPlaces);
  const userData = useSelector((state) => state.config.UserData);
  const firstLogin = useSelector((state) => state.config.FirstLoginReturn);
  const dataSharing = useSelector((state) => state.config.dataSharingConfig);
  
  const [data_sharing_consent, setData_sharing_consent] = useState(false);
  const [request_data_sharing_consent, setRequest_data_sharing_consent] = useState(false);
  const [policy_check, setPolicy_check] = useState(false);
  const [marketing_consent, setMarketing_consent] = useState(false);
  const [is_show_marketing_checkbox, setIs_show_marketing_checkbox] = useState(false);

  const hasUserData = !!(userData || firstLogin?.userData);
  const user = userData || firstLogin?.userData || null;
  const extraUserData = firstLogin || null;
  const userId = user?.user_id || firstLogin?.id;
  const ccDigits = firstLogin?.last4Digits || firstLogin?.ccDigits || firstLogin?.last_digits || undefined;

  useEffect(() => {
    const existingDataSharingAgreement = firstLogin?.dataSharingAgreement ?? false;
    const existingMarketingAgreement = firstLogin?.isMarketingApproved ?? false;
    if (hasUserData && user) {
      setPolicy_check(userData !== false);
    }
    setData_sharing_consent(!existingDataSharingAgreement);
    setMarketing_consent(existingMarketingAgreement);
    setRequest_data_sharing_consent(!existingDataSharingAgreement);
    setIs_show_marketing_checkbox(!existingMarketingAgreement);
  }, [firstLogin, userData, hasUserData, user]);

  const citiesOptions = useMemo(() => {
    if (!citiesArr) return [];
    return Object.values(citiesArr).map((city) => ({
      id: city.id,
      label: city.text,
    }));
  }, [citiesArr]);

  const workPlacesOptions = useMemo(() => {
    if (!work_places) return [];
    return work_places.map((wp) => ({
      id: wp.id,
      label: wp.text,
    }));
  }, [work_places]);

  const handleOtpStateChange = (active) => {
    setIsOtpActive(active);
    if (onOtpStateChange) onOtpStateChange(active);
  };

  const onIsracardClick = () => {
    if (extraUserData?.dataSharingAgreement) return;
    const onIsracardConfirm = () => {
      setData_sharing_consent(true);
    };
    dispatch(addPopup({
      type: 'GENERAL_MESSAGE',
      text: ReactHtmlParser(dataSharing?.description_content, reactParserCommonOptions()),
      action: () => onIsracardConfirm(),
      className: 'normal'
    }));
  };

  const neededDataShareAgreement = !!(dataSharing && request_data_sharing_consent);

  return (
    <ThemeProvider theme={appTheme}>
      <Box
        testID={generateTestId(testID, 'form')}
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {!isOtpActive && (
          <Box sx={{ mb: isMobile ? 3 : 2, textAlign: 'center' }}>
            <TextHot
              size="xxxl"
              bold="extraBold"
              color="primary"
              testID={generateTestId(testID, 'title')}
            >
              {text?.['personal_details_title'] || 'פרטים אישיים'}
            </TextHot>
          </Box>
        )}

        <Box sx={{ display: isOtpActive ? 'none' : 'block', width: '100%' }} testID={generateTestId(testID, 'form-container')}>
          <PersonalInfoForm
            testID={generateTestId(testID, 'form')}
            userData={user}
            disabled={false}
            process={OTP_PROCESS.REGISTER}
            showRegistrationConsent={true}
            consentText={{
              personal_details_policy_err: text?.['personal_details_policy_err'],
              register_details_screen_terms_checkbox_html_web: text?.['register_details_screen_terms_checkbox_html_web'],
              marketing_consent_text: text?.['marketing_consent_text'],
              agreement_content_register: text?.['agreement_content_register'],
            }}
            extraUserData={extraUserData}
            isMobile={isMobile}
            hasUserData={hasUserData}
            policy_check={policy_check}
            setPolicy_check={setPolicy_check}
            is_show_marketing_checkbox={is_show_marketing_checkbox}
            marketing_consent={marketing_consent}
            setMarketing_consent={setMarketing_consent}
            neededDataShareAgreement={neededDataShareAgreement}
            data_sharing_consent={data_sharing_consent}
            setData_sharing_consent={setData_sharing_consent}
            onDataSharingInfoClick={onIsracardClick}
            userId={userId}
            ccDigits={ccDigits}
            submitLabel={translate('personal_details_btn')}
            citiesOptions={citiesOptions}
            workPlacesOptions={workPlacesOptions}
            onOtpStateChange={handleOtpStateChange}
            renderPhoneValidationExternally={renderPhoneValidationExternally}
            onOtpRequest={onOtpRequest}
            onRegistrationResponse={onRegistrationResponse}
            onCloseAllModals={onCloseAllModals}
          />
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default RegistrationForm;